import React, { useCallback, useMemo } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Alert, Box, Divider, paperClasses, Stack, Typography } from '@mui/material';
import { matchIsValidTel, type MuiTelInputCountry } from 'mui-tel-input';
import * as yup from 'yup';

import {
  CreateRoleAssignmentsRequestDTO,
  FolderDTOStatus,
  FolderType,
  getGetFolderRoleAssignmentsByUserQueryKey,
  RoleAssignmentDTO,
  useCreateRoleAssignments,
  useGetOrganizationBySlug,
  UserIdentifier
} from '@/api/generated';
import Field from '@/components/fields/Field';
import LocationTreeSelect from '@/components/fields/Location/LocationTreeSelect';
import { CommonFormFooter } from '@/components/Form/Form';
import { OrganizationStructureProvider, useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import { useSnackbar } from '@/hooks/useSnackbar';
import typography from '@/theme/typography';
import countries from '@/utils/countries';
import UserAccessTable from '@/views/CreateOrganizationItemModal/UserAccessTable';
import OrganizationDropdownSelect from '@/views/fields/OrganizationDropdownSelect/OrganizationDropdownSelect';
import ClientUserDropdownSelect from '@/views/fields/UserDropdownSelect/ClientUserDropdownSelect';
import SettingsModalForm from '@/views/SettingsModalForm/SettingsModalForm';

const ALLOWED_TYPES = [FolderType.FOLDER];

interface InviteClientUsersModalProps {
  onClose: () => void;
  defaultOrganizationSlug?: string;
  defaultLocationSlug?: string;
}

interface InviteClientUsersFormData {
  organization: string;
  locations: string[];
  assignments: {
    role: string;
    user: UserIdentifier;
    phone?: string;
  }[];
}

const InviteClientUsersModal: React.FC<InviteClientUsersModalProps> = ({
  onClose,
  defaultOrganizationSlug,
  defaultLocationSlug
}) => {
  const { t } = useTranslation(['users', 'common']);
  const { mutateAsync: createRoleAssignments } = useCreateRoleAssignments();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  const defaultValues: InviteClientUsersFormData = useMemo(
    () => ({
      organization: defaultOrganizationSlug ?? '',
      locations: defaultLocationSlug ? [defaultLocationSlug] : [],
      assignments: [
        {
          role: '',
          user: {} as UserIdentifier,
          phone: ''
        }
      ]
    }),
    [defaultOrganizationSlug, defaultLocationSlug]
  );

  const validationSchema = useMemo(
    () =>
      yup.object({
        organization: yup.string().min(1).required(),
        locations: yup.array().of(yup.string().min(1).required()).min(1).required(),
        assignments: yup
          .array()
          .of(
            yup.object({
              role: yup.string().min(1).required(),
              user: yup
                .object()
                .shape({
                  id: yup.string(),
                  email: yup.string().email(),
                  phone: yup.string()
                })
                .test('has-id-or-email', 'User is required', value => !!(value?.id || value?.email)),
              // Phone is required and must be valid when inviting a new user by email
              phone: yup.string().when('user', {
                is: (user: UserIdentifier) => user?.email && !user?.id,
                then: schema =>
                  schema
                    .required()
                    .test('valid-phone', 'Invalid phone number', value => !!value && matchIsValidTel(value)),
                otherwise: schema => schema.nullable()
              })
            })
          )
          .min(1)
          .required()
      }),
    []
  );

  const handleSend = useCallback(
    async (data: InviteClientUsersFormData) => {
      try {
        const roleAssignments: RoleAssignmentDTO[] = data.assignments
          .filter(assignment => assignment.user?.id || assignment.user?.email)
          .map(assignment => {
            // User is already a UserIdentifier object
            // For new users (email), add phone from the assignment
            const userPayload: UserIdentifier = assignment.user.email
              ? { email: assignment.user.email, phone: assignment.phone }
              : { id: assignment.user.id };

            return {
              users: [userPayload],
              roleIds: [assignment.role],
              locations: data.locations.map(location => ({
                organizationSlug: data.organization,
                slug: location,
                type: FolderType.FOLDER
              }))
            };
          });

        if (roleAssignments.length === 0) {
          showMessage(t('users:users.inviteClientUsers.noValidUsers'));

          return;
        }

        const requestData: CreateRoleAssignmentsRequestDTO = {
          roleAssignments
        };

        await createRoleAssignments({ data: requestData });

        showMessage(t('users:users.inviteClientUsers.successMessage'));

        await Promise.all(
          roleAssignments?.flatMap(assignment =>
            assignment.locations.map(location =>
              queryClient.invalidateQueries(
                getGetFolderRoleAssignmentsByUserQueryKey(location.organizationSlug!, location.slug!)
              )
            )
          )
        );
      } catch (error) {
        showMessage(t('common:error.somethingWentWrong'));
      }
    },
    [createRoleAssignments, queryClient, showMessage, t]
  );

  return (
    <SettingsModalForm
      isOpen
      onClose={onClose}
      closeOnSave={false}
      onSave={handleSend as (v: FieldValues) => Promise<void>}
      validationMode="onChange"
      head={
        <Typography component="h2" variant="h3">
          {t('users:users.inviteClientUsers.title')}
        </Typography>
      }
      sx={{
        [`& .${paperClasses.root}`]: {
          margin: 0,
          width: 700,
          height: 'unset'
        }
      }}
      defaultValues={defaultValues}
      validation={validationSchema}
    >
      <InviteClientUsersModalContent onClose={onClose} />
    </SettingsModalForm>
  );
};

interface InviteClientUsersModalContentProps {
  onClose: () => void;
}

const InviteClientUsersModalContent: React.FC<InviteClientUsersModalContentProps> = ({ onClose }) => {
  const { t } = useTranslation(['users', 'common']);
  const { watch, formState } = useFormContext<InviteClientUsersFormData>();
  const organization = watch('organization');
  const locations = watch('locations');
  const isValid = formState?.isValid;
  const { data: organizationData } = useGetOrganizationBySlug(organization, {}, { query: { enabled: !!organization } });

  const defaultCountry = useMemo(() => {
    const orgCountries = organizationData?.countries;
    const firstCountry = Array.isArray(orgCountries) ? orgCountries[0] : orgCountries;
    if (!firstCountry) return undefined;

    // Convert alpha-3 country code to alpha-2 for phone input
    return countries.alpha3ToAlpha2(firstCountry) as MuiTelInputCountry | undefined;
  }, [organizationData?.countries]);

  return (
    <OrganizationStructureProvider organizationSlug={organization}>
      <Box sx={{ p: 4, pt: 0, '& .MuiFormControl-root': { p: 0, mb: 0 } }}>
        <Stack direction="row" alignItems="center" justifyContent="center" mb={2}>
          <Box component="img" src="/images/invite-client-users.svg" />
        </Stack>
        <Stack gap={1} mb={2}>
          <Field
            name="organization"
            label={t('users:users.inviteClientUsers.organization')}
            component={OrganizationDropdownSelect}
            withFormControl
            markAsRequired
            innerProps={{
              isMulti: false,
              placeholder: t('users:users.inviteClientUsers.selectOrganization')
            }}
          />

          <Field
            name="locations"
            label=""
            component={LocationTreeSelect}
            withFormControl
            outerSx={{
              '& .MuiFormLabel-root': {
                display: 'none'
              }
            }}
            innerProps={{
              isMulti: true,
              allowedTypes: ALLOWED_TYPES,
              organizationSlug: organization,
              placeholder: t('users:users.inviteClientUsers.selectFolder')
            }}
          />
        </Stack>

        <Divider />
        <OrganizationInfo />

        <Divider sx={{ mb: 2 }} />

        {organization && locations.length > 0 && (
          <UserAccessTable
            name="assignments"
            organizationId={organizationData?.id}
            enforceUniqueUsers
            defaultCountry={defaultCountry}
          />
        )}
      </Box>
      <CommonFormFooter
        onReset={onClose}
        resetText={t('common:actions.cancel')}
        submitText={t('users.inviteClientUsers.invite')}
        disableSubmit={!isValid}
      />
    </OrganizationStructureProvider>
  );
};

const OrganizationInfo = () => {
  const { t } = useTranslation(['users', 'common']);
  const { watch } = useFormContext<InviteClientUsersFormData>();

  const organization = watch('organization');
  const locations = watch('locations');
  const { mapStructure } = useOrganizationStructure(organization);
  const { data: organizationData } = useGetOrganizationBySlug(organization);

  const locationData = useMemo(() => locations.map(location => mapStructure[location]), [locations, mapStructure]);

  const isActive = useMemo(
    () => (locationData ?? [])?.every(location => location?.status === FolderDTOStatus.ACTIVE),
    [locationData]
  );
  const admins = useMemo(
    () => organizationData?.admins?.filter(admin => admin?.id)?.map(admin => admin.id!) ?? [],
    [organizationData]
  );

  if (!organization || locations.length === 0) {
    return (
      <Alert severity="info" sx={{ '&.MuiPaper-root': { width: 'unset', my: 2 } }}>
        {t('users:users.inviteClientUsers.info')}
      </Alert>
    );
  }

  if (!isActive) {
    return (
      <Alert severity="error" sx={{ '&.MuiPaper-root': { width: 'unset', my: 2 } }}>
        {t('users:users.inviteClientUsers.info')}
      </Alert>
    );
  }

  return (
    <Stack direction="row" gap={2} alignItems="center" my={1}>
      <Typography variant="body3" fontWeight={typography.fontWeightMedium} sx={{ color: 'grey.700' }}>
        {t('users:users.inviteClientUsers.admins')}
      </Typography>
      <ClientUserDropdownSelect
        value={admins}
        name="users"
        isMulti
        isDisabled
        sx={{
          '& .DropdownSelect-clearIcon': {
            display: 'none'
          }
        }}
      />
    </Stack>
  );
};

export default InviteClientUsersModal;
